import { isNotNil } from '@wrisk/ui-components'
import { isNil } from 'lodash-es'

import {
  AddCoverDisclosureChange,
  getCoverLevelOption,
  getCurrentQuote,
  hasCoverOptionItem,
  isMatchingCoverLevelOption,
  PolicyChangeType,
  Proposal,
  ProposalAdjustmentRequest,
  RemoveCoverDisclosureChange,
} from '../../../../domain'

const getCoverOptionLevelChanges = (
  current: Proposal,
  draft: Proposal,
): ProposalAdjustmentRequest['changes'] => {
  const currentQuote = getCurrentQuote(current)
  const draftQuote = getCurrentQuote(draft)

  const draftCoverLevelOption = getCoverLevelOption(draftQuote)
  const currentCoverLevelOption = getCoverLevelOption(currentQuote)

  const isMatch = isMatchingCoverLevelOption(currentCoverLevelOption)

  if (!draftCoverLevelOption) {
    return []
  }

  return isMatch(draftCoverLevelOption)
    ? []
    : [
        {
          changeType: PolicyChangeType.SELECT_COVER_LEVEL,
          coverLevel: draftCoverLevelOption.coverLevel,
        },
        {
          changeType: PolicyChangeType.SELECT_PAYMENT_SCHEDULE,
          paymentScheduleType: draftCoverLevelOption.paymentScheduleType,
        },
      ]
}

const getCoverOptionItemChanges = (
  current: Proposal,
  draft: Proposal,
): ProposalAdjustmentRequest['changes'] => {
  const currentCoverLevelOption = getCoverLevelOption(current.quote)
  const draftCoverLevelOption = getCoverLevelOption(draft.quote)

  return (
    draftCoverLevelOption?.coverOptionItems
      .map((draftItem) => {
        const currentItem = currentCoverLevelOption?.coverOptionItems.find(
          hasCoverOptionItem(draftItem.coverOption),
        )

        return draftItem?.active === currentItem?.active || isNil(draftItem?.active)
          ? undefined
          : draftItem?.active
          ? ({
              changeType: PolicyChangeType.ADD_COVER,
              coverOption: draftItem.coverOption,
            } satisfies AddCoverDisclosureChange)
          : ({
              changeType: PolicyChangeType.REMOVE_COVER,
              coverOption: draftItem.coverOption,
            } satisfies RemoveCoverDisclosureChange)
      })
      .filter(isNotNil) ?? []
  )
}

export const getDraftChanges = (
  current: Proposal,
  draft: Proposal,
): ProposalAdjustmentRequest['changes'] => [
  ...getCoverOptionLevelChanges(current, draft),
  ...getCoverOptionItemChanges(current, draft),
]
